import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import type { ProductGroup } from '../lib/FinanceFilters.js';

interface CronHealth {
  workflows: Array<{
    workflow: string;
    status: 'ok' | 'error';
    error_message: string | null;
    details: string | null;
    timestamp: string;
    errors_7d: number;
  }>;
  recentErrors: Array<{
    workflow: string;
    error_message: string;
    timestamp: string;
  }>;
}

interface PeriodMetrics {
  key: string;
  label: string;
  start: string;
  end: string;
  row: 'standard' | 'monthly';
  revenue: number;
  fees: number;
  pnl: number;
  orders: number;
  units: number;
  refunds: number;
  ppcOrders: number;
  ppcSales: number;
  adSpend: number;
  margin: number;
  acos: number;
  tacos: number;
  estPayout: number;
}

interface MarketplaceInfo {
  id: string;
  label: string;
  currency: string;
}

const PERIOD_COLORS: Record<string, string> = {
  today: 'text-blue-600',
  yesterday: 'text-amber-600',
  '7d': 'text-orange-500',
  '14d': 'text-purple-600',
  '30d': 'text-red-500',
  mtd: 'text-emerald-600',
  'month-1': 'text-blue-600',
  'month-2': 'text-teal-600',
  'month-3': 'text-orange-500',
  'month-4': 'text-purple-600',
  'month-5': 'text-red-500',
  custom: 'text-indigo-600',
};

const PERIOD_BORDERS: Record<string, string> = {
  today: 'border-t-blue-400',
  yesterday: 'border-t-amber-400',
  '7d': 'border-t-orange-400',
  '14d': 'border-t-purple-400',
  '30d': 'border-t-red-400',
  mtd: 'border-t-emerald-400',
  'month-1': 'border-t-blue-400',
  'month-2': 'border-t-teal-400',
  'month-3': 'border-t-orange-400',
  'month-4': 'border-t-purple-400',
  'month-5': 'border-t-red-400',
  custom: 'border-t-indigo-400',
};

const MP_FLAGS: Record<string, string> = {
  ATVPDKIKX0DER: '🇺🇸', A2EUQ1WTGCTBG2: '🇨🇦', A1AM78C64UM0Y8: '🇲🇽',
  A1F83G8C2ARO7P: '🇬🇧', A1PA6795UKMFR9: '🇩🇪', A13V1IB3VIYZZH: '🇫🇷',
  APJ6JRA9NG5V4: '🇮🇹', A1RKKUPIHCS9HS: '🇪🇸', A1805IZSGTT6HS: '🇳🇱', A2NODRKZP88ZB9: '🇸🇪',
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', CAD: 'C$', MXN: 'MX$', GBP: '£', EUR: '€', SEK: 'kr',
};

function getCustomPresets() {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const today = fmt(now);
  const q = Math.floor(now.getMonth() / 3);
  const qStart = new Date(now.getFullYear(), q * 3, 1);
  const lastQStart = new Date(now.getFullYear(), (q - 1) * 3, 1);
  const lastQEnd = new Date(now.getFullYear(), q * 3, 0);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  return [
    { label: 'This Q', start: fmt(qStart), end: today },
    { label: 'Last Q', start: fmt(lastQStart), end: fmt(lastQEnd) },
    { label: 'YTD', start: fmt(yearStart), end: today },
    { label: '90d', start: fmt(new Date(now.getTime() - 90 * 86400000)), end: today },
  ];
}

export function Overview() {
  const [periods, setPeriods] = useState<PeriodMetrics[]>([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [marketplace, setMarketplace] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [localCurrency, setLocalCurrency] = useState('USD');
  const [showUsd, setShowUsd] = useState(true);
  const [marketplaces, setMarketplaces] = useState<MarketplaceInfo[]>([]);
  const [products, setProducts] = useState<ProductGroup[]>([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  // Applied custom range (triggers API) vs draft inputs (local only until Apply)
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd, setAppliedEnd] = useState('');
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [cronHealth, setCronHealth] = useState<CronHealth | null>(null);

  const applyCustomRange = (start: string, end: string) => {
    setDraftStart(start);
    setDraftEnd(end);
    setAppliedStart(start);
    setAppliedEnd(end);
    setShowCustomPicker(false);
  };

  const clearCustomRange = () => {
    setDraftStart('');
    setDraftEnd('');
    setAppliedStart('');
    setAppliedEnd('');
    setShowCustomPicker(false);
  };

  useEffect(() => {
    setRefreshing(true);
    const qp = new URLSearchParams();
    if (marketplace) qp.set('marketplace', marketplace);
    if (!showUsd && marketplace) qp.set('currency', 'local');
    if (selectedSku) qp.set('sku', selectedSku);
    else if (selectedProduct) qp.set('parentAsin', selectedProduct);
    if (appliedStart && appliedEnd) {
      qp.set('mode', 'custom');
      qp.set('startDate', appliedStart);
      qp.set('endDate', appliedEnd);
    }
    const qs = qp.toString() ? `?${qp}` : '';

    // Load products once
    if (products.length === 0) {
      api.get<{ id: string; is_active: number }[]>('/accounts').then(accts => {
        const active = accts.filter(a => a.is_active);
        if (active.length > 0) {
          api.get<{ products: ProductGroup[] }>(`/finance/products/${active[0].id}`).then(d => {
            if (d.products?.length) setProducts(d.products);
          }).catch(() => {});
        }
      }).catch(() => {});
    }

    api.get<{ periods: PeriodMetrics[]; baseCurrency: string; localCurrency: string; activeMarketplaces: MarketplaceInfo[] }>(`/sales/dashboard${qs}`)
      .then((data) => {
        setPeriods(data.periods);
        setCurrency(data.baseCurrency);
        setLocalCurrency(data.localCurrency);
        if (!marketplace && data.activeMarketplaces) {
          setMarketplaces(data.activeMarketplaces);
        }
      })
      .catch(() => {})
      .finally(() => { setInitialLoad(false); setRefreshing(false); });
  }, [marketplace, showUsd, selectedProduct, selectedSku, appliedStart, appliedEnd]);

  // Fetch cron health (once on mount)
  useEffect(() => {
    api.get<CronHealth>('/admin/cron-health')
      .then(setCronHealth)
      .catch(() => {});
  }, []);

  const curSymbol = CURRENCY_SYMBOLS[currency] ?? '$';

  if (initialLoad && periods.length === 0) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Marketplace selector + currency toggle + product selector */}
      {marketplaces.length > 0 && (<>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setMarketplace(''); setShowUsd(true); }}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition ${
              !marketplace ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {marketplaces.map((mp) => (
            <button
              key={mp.id}
              onClick={() => setMarketplace(mp.id)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition ${
                marketplace === mp.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {MP_FLAGS[mp.id] ?? ''} {mp.label}
            </button>
          ))}

          {/* Currency toggle */}
          <span className="text-gray-300 mx-1">|</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowUsd(true)}
              className={`px-2.5 py-1 text-xs font-medium transition ${
                showUsd ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              USD
            </button>
            {marketplace && localCurrency !== 'USD' && (
              <button
                onClick={() => setShowUsd(false)}
                className={`px-2.5 py-1 text-xs font-medium transition border-l border-gray-200 ${
                  !showUsd ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {localCurrency}
              </button>
            )}
          </div>
          <span className="text-xs text-gray-400">
            Showing {currency}
          </span>
        </div>

{/* intentionally removed period mode selector — unified view */}

        {/* Product buttons — separate row */}
        {products.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-gray-400 mr-1">Product:</span>
            <button onClick={() => { setSelectedProduct(''); setSelectedSku(''); }}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${!selectedProduct ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              All
            </button>
            {products.map(p => (
              <button key={p.parentAsin} onClick={() => { setSelectedProduct(p.parentAsin === selectedProduct ? '' : p.parentAsin); setSelectedSku(''); }}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${selectedProduct === p.parentAsin ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {p.title || p.parentAsin}
              </button>
            ))}
          </div>
        )}

        {/* Variation buttons — separate row */}
        {selectedProduct && (() => {
          const group = products.find(p => p.parentAsin === selectedProduct);
          return group && group.children.length > 1 ? (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-gray-400 mr-1">Variation:</span>
              <button onClick={() => setSelectedSku('')}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${!selectedSku ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                All
              </button>
              {group.children.map(ch => (
                <button key={ch.sku} onClick={() => setSelectedSku(ch.sku === selectedSku ? '' : ch.sku)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${selectedSku === ch.sku ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {ch.label}
                </button>
              ))}
            </div>
          ) : null;
        })()}
      </>)}

      {/* Time-period KPI cards — unified view */}
      {periods.length > 0 ? (() => {
        const standardCards = periods.filter(p => p.row === 'standard');
        const monthlyCards = periods.filter(p => p.row === 'monthly');
        return (
          <div className={`space-y-4 transition-opacity duration-200 ${refreshing ? 'opacity-50' : ''}`}>
            {/* Row 1: Standard periods */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
              {standardCards.map((p) => (
                <PeriodCard key={p.key} period={p} curSymbol={curSymbol} />
              ))}
            </div>
            {/* Row 2: Custom/MTD + last 4 months */}
            {monthlyCards.length > 0 && (
              <>
                <div className="flex items-center gap-2 pt-2">
                  <h3 className="text-sm font-semibold text-gray-500">Monthly</h3>
                  <span className="text-gray-300 mx-0.5">|</span>
                  {/* Quick presets */}
                  <div className="flex items-center gap-1">
                    {getCustomPresets().map(preset => (
                      <button key={preset.label} onClick={() => applyCustomRange(preset.start, preset.end)}
                        className={`px-2.5 py-1 text-xs rounded-lg font-medium transition ${
                          appliedStart === preset.start && appliedEnd === preset.end
                            ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}>{preset.label}</button>
                    ))}
                    <button onClick={() => setShowCustomPicker(!showCustomPicker)}
                      className={`px-2.5 py-1 text-xs rounded-lg font-medium transition ${
                        showCustomPicker || (appliedStart && !getCustomPresets().some(p => p.start === appliedStart && p.end === appliedEnd))
                          ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}>Custom</button>
                    {appliedStart && appliedEnd && (
                      <button onClick={clearCustomRange}
                        className="px-1.5 py-1 text-xs text-gray-400 hover:text-gray-600 rounded">✕</button>
                    )}
                  </div>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
                {/* Collapsible custom date picker */}
                {showCustomPicker && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <input type="date" value={draftStart}
                      onChange={e => setDraftStart(e.target.value)}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600" />
                    <span className="text-xs text-gray-400">to</span>
                    <input type="date" value={draftEnd}
                      onChange={e => setDraftEnd(e.target.value)}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600" />
                    <button
                      onClick={() => { if (draftStart && draftEnd) applyCustomRange(draftStart, draftEnd); }}
                      disabled={!draftStart || !draftEnd}
                      className="px-3 py-1 text-xs rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >Apply</button>
                    <button onClick={() => setShowCustomPicker(false)}
                      className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                )}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
                  {monthlyCards.map((p) => (
                    <PeriodCard key={p.key} period={p} curSymbol={curSymbol} />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })() : null}

      {/* System Health — link to dedicated page */}
      {cronHealth && cronHealth.recentErrors.length > 0 && (
        <a href="/system-health" className="block rounded-xl border border-red-200 bg-red-50 p-4 hover:bg-red-100 transition">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-medium text-red-700">{cronHealth.recentErrors.length} system error{cronHealth.recentErrors.length !== 1 ? 's' : ''} in last 24h</span>
            <span className="text-xs text-red-500 ml-auto">View System Health &rarr;</span>
          </div>
        </a>
      )}
    </div>
  );
}

function PeriodCard({ period: p, curSymbol }: { period: PeriodMetrics; curSymbol: string }) {
  const color = PERIOD_COLORS[p.key] ?? 'text-gray-700';
  const border = PERIOD_BORDERS[p.key] ?? 'border-t-gray-300';
  const dateRange = p.start === p.end
    ? fmtDate(p.start)
    : `${fmtDate(p.start)} — ${fmtDate(p.end)}`;

  const fmt = (n: number) => fmtCur(n, curSymbol);

  return (
    <div className={`rounded-xl border border-gray-200 border-t-4 ${border} bg-white p-4 space-y-4`}>
      {/* Header */}
      <div>
        <h3 className={`text-lg font-bold ${color}`}>{p.label}</h3>
        <p className="text-[11px] text-gray-400">{dateRange}</p>
      </div>

      {/* Sales + Fees + P&L */}
      <div className="space-y-2">
        <MetricRow label="Sales" value={fmt(p.revenue)} />
        <MetricRow label="Account fees" value={fmt(p.fees)} />
        <MetricRow
          label="Overall P&L"
          value={fmt(p.pnl)}
          warn={p.pnl < 0}
        />
      </div>

      <hr className="border-gray-100" />

      {/* Orders / Units / Refunds */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Orders / Units / Refunds</span>
        </div>
        <p className="text-sm font-semibold text-gray-900">
          {p.orders} / {p.units} / {p.refunds}
        </p>
      </div>

      {/* PPC */}
      <div className="space-y-2">
        <MetricRow label="PPC Orders" value={String(p.ppcOrders)} />
        <MetricRow label="Margin" value={p.revenue > 0 ? `${p.margin}%` : '—'} />
        <MetricRow label="Ad Spend" value={fmt(p.adSpend)} />
        <MetricRow label="ACOS" value={p.ppcSales > 0 ? `${p.acos}%` : '—'} />
        <MetricRow label="Est. Payout" value={fmt(p.estPayout)} />
        <MetricRow label="TACOS" value={p.revenue > 0 ? `${p.tacos}%` : '—'} />
      </div>
    </div>
  );
}

function MetricRow({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-sm font-semibold ${warn ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
        {warn && <span className="ml-1 text-red-500 text-xs">&#9888;</span>}
      </span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-80 rounded-xl bg-gray-200" />
        ))}
      </div>
    </div>
  );
}

function fmtCur(n: number, symbol = '$'): string {
  const abs = Math.abs(n);
  const formatted = abs < 1000
    ? abs.toFixed(2)
    : abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
